/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 客户端下载抽屉组件
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-10 16:42:15
 */
import './index.scss'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Drawer, Progress, Button, message, Modal } from 'antd'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  setClientDownloadDrawerVisible,
  setClientDownloadTasks,
  setClientDownloadProgress
} from '../../../actions/photo'
import type { ClientDownloadTask } from '../../../slices/photoSlice'
import { getOSSToken } from '../../../actions/main'
import download_task_icon from '../../../images/download_task.png'
import photoResultFeedbackIcon from '../../../images/photo-result-feedback-icon.png'
import sensors from '../../../utils/sensors'
import desktopLib from '../../../utils/desktopLib'
import { supportsNewDownloadFeature } from '../../../utils/versionUtils'
import ContactUS from '../ContactUS'

interface ClientDownloadDrawerProps {
  dataSource?: string
}

const Header = () => (
  <div className="client-download-header">
    <h3>客户端下载记录</h3>
    <div
      style={{ fontSize: 14, fontWeight: 400, color: '#818592', marginTop: 8 }}
    >
      照片将直接下载到本地文件夹，记录保存7天
    </div>
  </div>
)

const TaskItem: React.FC<{
  task: ClientDownloadTask
  onTaskUpdate: (taskId: string, updates: Partial<ClientDownloadTask>) => void
  allTasks: ClientDownloadTask[]
  onTrackAction?: (actionType: string, params?: any) => void
  isLatestCompleted?: boolean
}> = ({ task, onTaskUpdate, allTasks, onTrackAction, isLatestCompleted }) => {
  const dispatch = useAppDispatch()
  const group = useAppSelector((state) => state.group)
  const user = useAppSelector((state) => state.main.user)
  const [uploadingLogs, setUploadingLogs] = useState(false)

  const isNewDownloadUIEnabled =
    desktopLib.isDesktop && supportsNewDownloadFeature('1.3.2')
  const failedCount = Math.max(
    0,
    (task.totalCount || 0) - (task.downloadedCount || 0)
  )
  const hasPartialFailure =
    isNewDownloadUIEnabled &&
    task.status === 'completed' &&
    (task.totalCount || 0) > 0 &&
    failedCount > 0

  // 下载完成满意度问卷控制
  const surveyStorageKey = `client_download_survey_done_${task.id}`
  const surveyDone =
    typeof window !== 'undefined'
      ? localStorage.getItem(surveyStorageKey)
      : null
  const shouldShowSurvey =
    isLatestCompleted &&
    task.status === 'completed' &&
    !hasPartialFailure &&
    !surveyDone

  const openSurvey = () => {
    const userID = user?.user_id || ''
    const nickname = user?.nickname || ''
    const inviteID = group ? group.invite_id || group.groupId || '' : ''
    const groupName = group ? group.groupName || '' : ''
    const surveyUrl = `https://alidocs.dingtalk.com/notable/share/form/v01Mp7ld7bEo9xJ7OBQ_hERWDMS_WyPvuh8?prefill_UserID=${encodeURIComponent(
      userID || ''
    )}&prefill_%E5%A7%93%E5%90%8D=${encodeURIComponent(
      nickname || ''
    )}&prefill_%E5%9B%A2%E9%98%9F%E5%8F%B7=${encodeURIComponent(
      inviteID || ''
    )}&prefill_%E5%9B%A2%E9%98%9F%E5%90%8D=${encodeURIComponent(
      groupName || ''
    )}`

    if (desktopLib.isDesktop) {
      desktopLib.openContact(surveyUrl)
    } else {
      window.open(surveyUrl, '_blank')
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem(surveyStorageKey, '1')
    }
  }

  // 生成任务标题
  const getTaskTitle = useCallback(() => {
    // 使用 moment 将 UTC 时间转换为北京时间
    const dateStr = dayjs(task.createdAt)
      .utcOffset(8)
      .format('YYYY/MM/DD HH:mm:ss')

    if (task.status === 'downloading' || task.status === 'paused') {
      // 下载中或暂停状态：显示进度
      return `全部照片(${task.downloadedCount}/${task.totalCount}张)_${dateStr}`
    } else if (task.status === 'completed') {
      // 完成状态：只显示总数
      return `全部照片(${task.totalCount}张)_${dateStr}`
    } else {
      // 其他状态：显示总数
      return `全部照片(${task.totalCount}张)_${dateStr}`
    }
  }, [task.status, task.downloadedCount, task.totalCount, task.createdAt])

  const handleOpenFolder = useCallback(() => {
    // 埋点上报
    if (onTrackAction) {
      onTrackAction('打开文件夹', {
        taskId: task.id,
        taskName: task.name
      })
    }

    if (desktopLib.isDesktop) {
      desktopLib
        .openFolder(task.savePath)
        .then((result) => {
          if (!result.success) {
            message.error(result.message)
          }
        })
        .catch(() => {
          message.error('打开文件夹失败')
        })
    }
  }, [task.savePath, task.id, task.name, onTrackAction])

  const handleUploadLogs = useCallback(async () => {
    if (uploadingLogs) return

    setUploadingLogs(true)
    const hide = message.loading('日志上报中...', 0)
    try {
      const ossToken = await dispatch(getOSSToken())
      const groupName = group.groupName
      const groupId = group.groupId
      const userId = user?.user_id
      const userName = user?.nickname

      await desktopLib.uploadLogs({
        ossToken,
        groupName,
        groupId,
        userId,
        userName
      })

      hide()
      message.success('日志上报成功，即将为您打开客服')
    } catch (error) {
      console.error('上传日志失败:', error)
      hide()
      message.error('日志上报失败，请检查网络后重试')
      throw error
    } finally {
      setUploadingLogs(false)
    }
  }, [dispatch, group, user, uploadingLogs])

  // 暂停任务
  const handlePause = useCallback(
    async (e) => {
      e.stopPropagation()

      // 埋点上报
      if (onTrackAction) {
        onTrackAction('暂停', {
          taskId: task.id,
          taskName: task.name
        })
      }

      try {
        if (desktopLib.isDesktop) {
          const result = await desktopLib.downloadPauseTask(task.id)

          if (result.success) {
            message.success('任务已暂停')
            onTaskUpdate(task.id, { status: 'paused' })
          } else {
            message.error(result.error || '暂停失败')
          }
        }
      } catch (error) {
        console.error('暂停任务失败:', error)
        message.error('暂停任务失败')
      }
    },
    [task.id, task.name, onTaskUpdate, onTrackAction]
  )

  // 继续任务
  const handleResume = useCallback(
    async (e) => {
      e.stopPropagation()

      // 埋点上报
      if (onTrackAction) {
        onTrackAction('继续', {
          taskId: task.id,
          taskName: task.name
        })
      }

      // 首先检查网络连接
      if (!navigator.onLine) {
        message.error('网络连接异常，无法继续下载任务')
        return
      }

      // 检查是否有正在进行的下载任务
      const hasRunningTask = allTasks.some(
        (t) => t.id !== task.id && t.status === 'downloading'
      )

      if (hasRunningTask) {
        message.warning('当前有任务正在下载中，请等待当前任务完成后再进行操作')
        return
      }

      try {
        if (desktopLib.isDesktop) {
          const result = await desktopLib.downloadResumeTask(task.id)

          if (result.success) {
            message.success('任务已继续')
            onTaskUpdate(task.id, { status: 'downloading' })
          } else {
            message.error(result.error || '继续失败')
          }
        }
      } catch (error) {
        console.error('继续任务失败:', error)
        message.error('继续任务失败')
      }
    },
    [task.id, task.name, onTaskUpdate, allTasks, onTrackAction]
  )

  // 删除任务
  const handleCancel = useCallback(
    async (e) => {
      e.stopPropagation()

      Modal.confirm({
        title: '是否删除下载任务',
        okText: '删除',
        cancelText: '取消',
        okType: 'danger',
        onOk: async () => {
          // 埋点上报
          if (onTrackAction) {
            onTrackAction('删除任务', {
              taskId: task.id,
              taskName: task.name
            })
          }

          try {
            if (desktopLib.isDesktop) {
              const result = await desktopLib.downloadCancelTask(task.id, true)

              if (result.success) {
                // 从UI中移除该任务
                onTaskUpdate(task.id, { status: 'deleted' })
              } else {
                message.error(result.error || '删除失败')
              }
            }
          } catch (error) {
            console.error('删除任务失败:', error)
            message.error('删除任务失败')
          }
        },
        onCancel: () => {
          // 取消操作，只关闭弹窗，不执行任何删除操作
        }
      })
    },
    [task.id, task.name, onTaskUpdate, onTrackAction]
  )

  // 添加重新下载的处理函数
  const handleRedownload = useCallback(
    async (e) => {
      e.stopPropagation()

      // 埋点上报
      if (onTrackAction) {
        onTrackAction('再次下载', {
          taskId: task.id,
          taskName: task.name
        })
      }

      // 首先检查网络连接
      if (!navigator.onLine) {
        message.error('网络连接异常，无法重新下载任务')
        return
      }

      // 检查是否有正在进行的下载任务
      const hasRunningTask = allTasks.some(
        (t) => t.id !== task.id && t.status === 'downloading'
      )

      if (hasRunningTask) {
        message.warning('当前有任务正在下载中，请等待当前任务完成后再进行操作')
        return
      }

      try {
        // 调用客户端重新下载API
        if (desktopLib.isDesktop) {
          const result = await desktopLib.downloadRedownloadTask(task.id)
          console.log('重新下载结果', result)

          if (result.success) {
            message.success('重新下载任务已启动')

            // 更新任务状态为下载中，保留 totalCount
            onTaskUpdate(task.id, {
              status: 'downloading',
              progress: 0,
              downloadedCount: 0,
              totalCount: result.data?.totalCount || task.totalCount || 0
            })
          } else {
            message.error(result.error || '重新下载失败')
          }
        }
      } catch (error) {
        console.error('重新下载失败:', error)
        message.error('重新下载失败')
      }
    },
    [task.id, task.name, onTaskUpdate, allTasks, onTrackAction]
  )

  return (
    <div className="client-task-item">
      <div className="task-header">
        <img src={download_task_icon} alt="任务图标" />
        <div className="task-info">
          <h4>{getTaskTitle()}</h4>
        </div>
      </div>

      {task.status === 'downloading' && (
        <div className="progress-section">
          <div className="progress-info">
            <span className="progress-label">下载进度：</span>
            <span className="progress-percent">{task.progress}%</span>
          </div>
          <Progress
            percent={task.progress}
            size="small"
            strokeColor={{
              from: '#71BEF8',
              to: '#0093FF'
            }}
            showInfo={false}
          />
          <div className="task-controls">
            <Button size="small" onClick={handlePause}>
              暂停
            </Button>
          </div>
        </div>
      )}

      {(task.status === 'paused' || task.status === 'pending') && (
        <div className="paused-section">
          <div className="paused-info">
            <span className="paused-text">任务已暂停</span>
          </div>
          <div className="task-controls">
            <Button type="primary" size="small" onClick={handleResume}>
              继续
            </Button>
            <Button size="small" onClick={handleCancel}>
              删除任务
            </Button>
          </div>
        </div>
      )}

      {hasPartialFailure && (
        <div className="error-section">
          <div className="error-text">部分照片下载失败</div>
          <div className="partial-summary">
            总数：{task.totalCount}，成功：{task.downloadedCount}，失败：
            {failedCount}
          </div>
          <div className="task-controls">
            <Button type="primary" size="small" onClick={handleOpenFolder}>
              打开文件夹
            </Button>
            <ContactUS
              fromPage="client_download_drawer_partial_fail"
              artificial={false}
              track={handleUploadLogs}
              style={{ display: 'inline-block', marginLeft: 8 }}
            >
              <Button size="small" loading={uploadingLogs}>
                联系客服
              </Button>
            </ContactUS>
            <Button
              size="small"
              onClick={handleRedownload}
              style={{ marginLeft: 8 }}
            >
              重新下载
            </Button>
          </div>
        </div>
      )}

      {!hasPartialFailure && task.status === 'completed' && (
        <div className="completed-section">
          <div className="completed-row">
            <div className="completed-info">
              <span className="progress-label">下载进度：</span>
              <span className="progress-percent">100%</span>
            </div>
            <div className="completed-buttons">
              <Button
                type="primary"
                size="small"
                onClick={handleOpenFolder}
                className="open-folder-btn"
              >
                打开文件夹
              </Button>
              <Button
                size="small"
                onClick={handleRedownload}
                className="download-again-btn"
              >
                再次下载
              </Button>
            </div>
          </div>
          <div className="progress-bar-completed">
            <div className="progress-fill"></div>
          </div>
          {shouldShowSurvey && (
            <div
              className="completed-survey"
              onClick={openSurvey}
              style={{
                cursor: 'pointer',
                marginTop: 8,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span style={{ marginRight: 6 }}>
                您喜欢新版本的照片下载体验吗？邀您填写问卷~
              </span>
              <img
                src={photoResultFeedbackIcon}
                alt="survey"
                style={{ width: 18, height: 18 }}
              />
            </div>
          )}
        </div>
      )}

      {task.status === 'failed' && (
        <div className="error-section">
          <div className="error-text">
            {task.error || '任务下载失败，请重新下载'}
          </div>
          <div className="task-controls">
            <Button type="primary" size="small" onClick={handleRedownload}>
              重新下载
            </Button>
          </div>
        </div>
      )}

      {task.status === 'cancelled' && (
        <div className="cancelled-section">
          <div className="cancelled-info">
            <span className="cancelled-text">任务已取消</span>
          </div>
          <div className="task-controls">
            <Button type="primary" size="small" onClick={handleRedownload}>
              重新下载
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

const ClientDownloadDrawer: React.FC<ClientDownloadDrawerProps> = ({
  dataSource
}) => {
  const dispatch = useAppDispatch()
  const visible = useAppSelector(
    (state) => state.photo.clientDownloadDrawerVisible
  )
  const clientDownloadTasks = useAppSelector(
    (state) => state.photo.clientDownloadTasks
  )

  const tasks = clientDownloadTasks || []

  // 记录上次的任务数量，用于判断是否需要上报埋点
  const lastTasksLengthRef = useRef(-1)

  // 添加埋点上报信息
  const handleDrawerTrack = useCallback(
    (actionType: string, params?: any) => {
      const downloadList = tasks.map((task, index) => {
        return {
          index,
          name:
            task.name ||
            `照片下载任务_${dayjs(task.createdAt)
              .utcOffset(8)
              .format('YYYY-MM-DD HH:mm:ss')}`,
          photosNum: task.totalCount,
          time: dayjs(task.createdAt).unix(),
          status: task.status,
          progress: task.progress
        }
      })
      const trackParams = {
        actionType: actionType,
        infoJson: JSON.stringify(downloadList),
        isDesktop: desktopLib.isDesktop,
        track_sign:
          'aother.b4414.action.web_get_action_photo_all_download_record',
        requirement_id: 382,
        ...params
      }
      if (dataSource) {
        trackParams.fromPlace =
          dataSource === 'precise' ? 'preciseSearch' : 'watermarkContent'
      }
      sensors.track('web_get_action_photo_all_download_record', trackParams)
    },
    [tasks, dataSource]
  )

  // 从客户端获取下载历史记录
  useEffect(() => {
    if (visible && desktopLib.isDesktop) {
      const groupID = localStorage.getItem('group_id')
      if (groupID) {
        desktopLib
          .downloadGetTasks(groupID)
          .then((result) => {
            if (result.success && result.data) {
              // 将客户端任务转换为前端格式
              const clientTasks = result.data.map((task) => ({
                id: task.id,
                name:
                  task.name ||
                  `照片下载任务_${dayjs(task.createdAt)
                    .utcOffset(8)
                    .format('YYYY-MM-DD HH:mm:ss')}`,
                status: task.status,
                progress: task.progress || 0,
                downloadedCount: task.downloadedCount || 0,
                totalCount: task.totalCount || 0,
                savePath: task.savePath || task.config?.downloadDir,
                createdAt: task.createdAt,
                error: task.error
              }))

              // 过滤掉超过7天的任务
              const sevenDaysAgo = dayjs().subtract(7, 'days')

              const recentTasks = clientTasks
                .filter((task) => {
                  if (task.status === 'failed') {
                    // 如果失败并且两个为0，过滤掉
                    return !(
                      task.downloadedCount === 0 && task.totalCount === 0
                    )
                  }
                  return task
                })
                .filter((task) => {
                  return dayjs(task.createdAt).isAfter(sevenDaysAgo)
                })

              // 更新任务列表
              dispatch(setClientDownloadTasks(recentTasks))
            }
          })
          .catch((error) => {
            console.error('获取客户端下载历史失败:', error)
          })
      }
    }
  }, [visible, dispatch])

  // 单独处理显示埋点上报，避免依赖循环
  useEffect(() => {
    if (visible && tasks.length >= 0) {
      // 只有当任务数量发生变化时才上报埋点
      if (tasks.length !== lastTasksLengthRef.current) {
        handleDrawerTrack('show')
        lastTasksLengthRef.current = tasks.length
      }
    }
  }, [visible, tasks.length, handleDrawerTrack])

  // 监听客户端下载进度更新
  useEffect(() => {
    if (visible && desktopLib.isDesktop) {
      const handleProgressUpdate = (event, data) => {
        dispatch(setClientDownloadProgress(data))
      }

      const handleDownloadCompleted = (event, data) => {
        console.log('下载完成', data)
        dispatch(setClientDownloadProgress(data))
      }

      const handleNetworkError = (event, data) => {
        console.log('网络错误', data)
        dispatch(setClientDownloadProgress(data))
      }

      // 注册事件监听
      desktopLib.onDownloadProgressUpdate(handleProgressUpdate)
      desktopLib.onDownloadCompleted(handleDownloadCompleted)
      desktopLib.onDownloadNetworkError(handleNetworkError)

      return () => {
        // 清理事件监听
        if (desktopLib.isDesktop) {
          desktopLib.removeDownloadListeners('download:progress-update')
          desktopLib.removeDownloadListeners('download:completed')
          desktopLib.removeDownloadListeners('download:network-error')
        }
      }
    }
  }, [visible, dispatch])

  const handleClose = useCallback(() => {
    dispatch(setClientDownloadDrawerVisible(false))
    handleDrawerTrack('close')
    // 关闭抽屉时重置任务数量记录
    lastTasksLengthRef.current = -1
  }, [dispatch, handleDrawerTrack])

  // 添加任务更新处理函数
  const handleTaskUpdate = useCallback(
    (taskId: string, updates: Partial<ClientDownloadTask>) => {
      const currentTasks = clientDownloadTasks || []

      // 如果状态是deleted，则从列表中移除该任务
      if (updates.status === 'deleted') {
        const updatedTasks = currentTasks.filter((task) => task.id !== taskId)
        dispatch(setClientDownloadTasks(updatedTasks))
      } else {
        // 否则更新任务
        const updatedTasks = currentTasks.map((task) =>
          task.id === taskId ? { ...task, ...updates } : task
        )
        dispatch(setClientDownloadTasks(updatedTasks))
      }
    },
    [clientDownloadTasks, dispatch]
  )

  return (
    <Drawer
      className="client-download-drawer"
      getContainer={false}
      closable={true}
      keyboard={false}
      title={<Header />}
      size={415}
      open={visible}
      onClose={handleClose}
    >
      <div className="client-task-list">
        {tasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-text">暂无下载任务</div>
          </div>
        ) : (
          // 按创建时间倒序排列，最新的在前面（sort 是就地排序，store 数组被
          // Immer 冻结，必须先复制副本再排，否则渲染期抛 TypeError 导致白屏）
          [...tasks]
            .sort(
              (a, b) =>
                dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
            )
            .map((task, index) => {
              const isLatestCompleted =
                index === 0 &&
                task.status === 'completed' &&
                (task.totalCount || 0) > 0 &&
                task.downloadedCount === task.totalCount
              return (
                <TaskItem
                  key={task.id}
                  task={task}
                  onTaskUpdate={handleTaskUpdate}
                  allTasks={tasks}
                  onTrackAction={handleDrawerTrack}
                  isLatestCompleted={isLatestCompleted}
                />
              )
            })
        )}
      </div>
    </Drawer>
  )
}

export default ClientDownloadDrawer
